import { z } from 'zod';
import { LocalizedStringSchema } from './car';
/**
 * Resale-marketplace listing contract (Phase 18 — API SKELETON).
 *
 * A listing is a single car offered for resale on the CarsFinders marketplace.
 * It exists in one of TWO legally-distinct modes (see the market research
 * §5 tax-ladder note) — the `listingMode` DISCRIMINATOR is the source of truth:
 *
 *  - `own_stock`     — CarsFinders is the DEALER-OF-RECORD. The car came from a
 *                      won auction lot (`won_cars`), we hold it, we carry full
 *                      liability. `sellerId` is the platform sentinel
 *                      ('platform'); `sourceCar` links back to the won_cars
 *                      provenance. This is the ONLY legally-safe way to resell
 *                      systematically (a private person's 3rd+ sale/year is
 *                      taxed 23% — own stock must be sold as a legal entity).
 *  - `consignment`   — a CLIENT sells their own car; CarsFinders only provides
 *                      marketing / inspection / facilitation (BaT-style — NOT
 *                      escrow, NOT dealer-of-record). `sellerId` is the client's
 *                      userId. A consignment fee (5–10%, capped) is charged ONLY
 *                      on `sold` — see pricing-rules.ts `marketplace`.
 *
 * ⛔ Money-flow gating: no fee is ever *settled* here. The consignment fee can be
 * COMPUTED (pure) but any live ledger write stays behind the Phase-13
 * `PAYMENTS_ENABLED` gate (inert by default), exactly like dealer billing.
 */
/** The platform sentinel used as `sellerId` for own-stock listings. */
export const PLATFORM_SELLER_ID = 'platform';
/** The two legally-distinct listing modes (the schema discriminator). */
export const ListingModeSchema = z.enum(['own_stock', 'consignment']);
export const LISTING_MODES = ListingModeSchema.options;
/**
 * Listing lifecycle states (market research §4):
 *   draft → moderation → active → reserved → sold → archived  (+ rejected)
 * `archived` is terminal.
 */
export const ListingStatusSchema = z.enum([
    'draft', // seller is still editing; not visible publicly
    'moderation', // submitted; awaiting admin auto-checks + human review
    'active', // approved and publicly listed
    'reserved', // a buyer is in negotiation / soft-hold
    'sold', // deal closed (consignment fee charged here, gated)
    'rejected', // moderation declined (mandatory reason)
    'archived', // terminal — de-listed / expired / sold-and-closed
]);
export const LISTING_STATUSES = ListingStatusSchema.options;
/** The default status of a listing document written without an explicit status. */
export const DEFAULT_LISTING_STATUS = 'draft';
/** Normalize an unknown/absent stored value to a ListingStatus. */
export function effectiveListingStatus(value) {
    const parsed = ListingStatusSchema.safeParse(value);
    return parsed.success ? parsed.data : DEFAULT_LISTING_STATUS;
}
/**
 * FORWARD transition matrix (without an admin override). Key = current status,
 * value = statuses it may advance to on the normal path.
 *
 * - draft → moderation (submit) | archived (abandon)
 * - moderation → active (approve) | rejected (decline)
 * - active → reserved (soft-hold) | sold | archived (de-list)
 * - reserved → sold | active (release the hold) | archived
 * - sold → archived (close out)
 * - rejected → draft (fix & resubmit) | archived (give up)
 * - archived → ∅ (terminal)
 */
export const LISTING_TRANSITIONS = {
    draft: ['moderation', 'archived'],
    moderation: ['active', 'rejected'],
    active: ['reserved', 'sold', 'archived'],
    reserved: ['active', 'sold', 'archived'],
    sold: ['archived'],
    rejected: ['draft', 'archived'],
    archived: [],
};
/**
 * Pure listing-status transition guard.
 *
 * - Normal path: `to` must be one of `from`'s allowed forward successors.
 * - Admin override: any `from → to` (backward / skip) is allowed, BUT a
 *   non-empty `reason` is mandatory (`OVERRIDE_REASON_REQUIRED` otherwise).
 * - A no-op (`from === to`) is always rejected (`SAME_STATE`).
 */
export function canListingTransition(from, to, options = {}) {
    const fromAllowed = LISTING_TRANSITIONS[from];
    const toAllowed = LISTING_TRANSITIONS[to];
    if (fromAllowed === undefined || toAllowed === undefined) {
        return { ok: false, code: 'UNKNOWN_STATE' };
    }
    if (from === to) {
        return { ok: false, code: 'SAME_STATE' };
    }
    if (options.adminOverride) {
        if (!options.reason || options.reason.trim() === '') {
            return { ok: false, code: 'OVERRIDE_REASON_REQUIRED' };
        }
        return { ok: true };
    }
    if (fromAllowed.length === 0) {
        return { ok: false, code: 'TERMINAL' };
    }
    if (!fromAllowed.includes(to)) {
        return { ok: false, code: 'FORWARD_ONLY' };
    }
    return { ok: true };
}
/** Currencies a listing price can be quoted in (UA market + import legs). */
export const ListingCurrencySchema = z.enum(['EUR', 'USD', 'UAH', 'CHF']);
/**
 * Provenance — the trust content that justifies the platform's margin/fee: the
 * auction lot it came from, the AI-analysis record, and any repair documents.
 * For own-stock this is auto-filled from the won_cars record; for consignment
 * it is whatever the client/platform attaches.
 */
export const ListingProvenanceSchema = z.object({
    /** Upstream auction lot id / listing_id (won_cars.listing_id for own-stock). */
    auctionLot: z.string().nullish(),
    /** Reference into `ai_car_analysis` (listing_id key). */
    aiAnalysisRef: z.string().nullish(),
    /** Repair-documentation references (S3 keys / doc ids). */
    repairDocs: z.array(z.string()).default([]),
});
/**
 * One superseded price point. The API appends the price a listing carried
 * BEFORE an edit changed it, stamped with the moment of the change — so the
 * array reads oldest-first and `priceHistory[0].price` is the original asking
 * price the detail page compares the current price against (price-drop badge).
 */
export const PriceHistoryEntrySchema = z.object({
    price: z.number().nonnegative(),
    /** When this price was superseded. */
    at: z.coerce.date().nullish(),
});
/** Fields shared by both listing modes. */
const listingCoreShape = {
    /** Public listing id (uuid). */
    listingId: z.string(),
    title: z.string().min(2).max(200),
    brand: z.string().min(1).max(80),
    model: z.string().min(1).max(80),
    year: z.number().int().min(1900).max(2100),
    price: z.number().nonnegative(),
    currency: ListingCurrencySchema,
    mileage: z.number().int().nonnegative(),
    vin: z.string().min(11).max(17).nullish(),
    photos: z.array(z.string()).default([]),
    description: LocalizedStringSchema.nullish(),
    status: ListingStatusSchema.default(DEFAULT_LISTING_STATUS),
    location: z.string().max(160).nullish(),
    /** Honest damage disclosure (VIN-report / auction "as bought" note). */
    damageDisclosure: z.string().max(4000).nullish(),
    provenance: ListingProvenanceSchema.default({ repairDocs: [] }),
    /** Seller analytics counters. */
    views: z.number().int().nonnegative().default(0),
    saves: z.number().int().nonnegative().default(0),
    leadCount: z.number().int().nonnegative().default(0),
    /** Admin moderation reason (set on `rejected`). */
    moderationReason: z.string().nullish(),
    /** Superseded prices, oldest first — appended by the API on every price edit. */
    priceHistory: z.array(PriceHistoryEntrySchema).default([]),
    /** First time the listing entered `active` (days-on-market anchor). */
    publishedAt: z.coerce.date().nullish(),
    /**
     * When the listing entered `sold` — the anchor the per-seller yearly
     * sales-count (and therefore the PIT tax ladder) is computed from.
     */
    soldAt: z.coerce.date().nullish(),
    createdAt: z.coerce.date().nullish(),
    updatedAt: z.coerce.date().nullish(),
};
/**
 * OWN-STOCK variant — CarsFinders is dealer-of-record. `sellerId` MUST be the
 * platform sentinel and `sourceCar` (the won_cars listing_id it originated
 * from) is required provenance.
 */
export const OwnStockListingSchema = z.object({
    listingMode: z.literal('own_stock'),
    sellerId: z.literal(PLATFORM_SELLER_ID),
    /** won_cars.listing_id this stock came from — required provenance link. */
    sourceCar: z.string(),
    ...listingCoreShape,
});
/**
 * CONSIGNMENT variant — a client sells their own car; `sellerId` is their
 * userId. `sourceCar` is optional (a consigned car may have no CarsFinders
 * auction provenance at all).
 */
export const ConsignmentListingSchema = z.object({
    listingMode: z.literal('consignment'),
    /** The consigning client's userId (never the platform sentinel). */
    sellerId: z.string().min(1),
    sourceCar: z.string().nullish(),
    ...listingCoreShape,
});
/**
 * A marketplace listing — discriminated on `listingMode`. Zod picks the right
 * variant so own-stock/consignment invariants (seller sentinel, required
 * provenance) are enforced at parse time.
 */
export const ListingSchema = z.discriminatedUnion('listingMode', [
    OwnStockListingSchema,
    ConsignmentListingSchema,
]);
/**
 * Seller-facing CREATE payload — the fields a seller controls. Server owns
 * listingId, status, counters, provenance auto-fill, and timestamps.
 */
export const CreateListingInputSchema = z.object({
    listingMode: ListingModeSchema,
    title: z.string().min(2).max(200),
    brand: z.string().min(1).max(80),
    model: z.string().min(1).max(80),
    year: z.number().int().min(1900).max(2100),
    price: z.number().nonnegative(),
    currency: ListingCurrencySchema,
    mileage: z.number().int().nonnegative(),
    vin: z.string().min(11).max(17).nullish(),
    photos: z.array(z.string()).max(50).default([]),
    description: LocalizedStringSchema.nullish(),
    location: z.string().max(160).nullish(),
    damageDisclosure: z.string().max(4000).nullish(),
    /** won_cars.listing_id — required for own-stock, ignored/optional otherwise. */
    sourceCar: z.string().nullish(),
});
/** Seller-facing PATCH payload — every field optional (partial edit of a draft). */
export const UpdateListingInputSchema = CreateListingInputSchema.partial().omit({
    listingMode: true,
});
/**
 * Lead channels a UA buyer can reach a seller through (market research §4).
 * ADDITIVE ONLY — a channel is persisted verbatim on every lead document and
 * keys `ListingAnalytics.leadsByChannel`, so removing or renaming one orphans
 * historical rows. `viber` joined `phone_reveal|telegram|callback` in Phase 18
 * (it is the second messenger UA private sellers actually answer on).
 */
export const LeadChannelSchema = z.enum(['phone_reveal', 'telegram', 'viber', 'callback']);
export const LEAD_CHANNELS = LeadChannelSchema.options;
/**
 * A buyer LEAD against a listing (`marketplace_leads` collection). Public —
 * a buyer reveals the phone, opens Telegram, or requests a callback; each is
 * logged and increments the listing's `leadCount`.
 */
export const LeadSchema = z.object({
    _id: z.unknown().nullish(),
    listingId: z.string(),
    channel: LeadChannelSchema,
    /** Optional contact details (required only for the callback channel by the API). */
    name: z.string().max(120).nullish(),
    phone: z.string().max(32).nullish(),
    message: z.string().max(2000).nullish(),
    createdAt: z.coerce.date().nullish(),
});
/** Public CREATE payload for a lead (POST .../leads). */
export const CreateLeadInputSchema = z
    .object({
    channel: LeadChannelSchema,
    name: z.string().min(2).max(120).nullish(),
    phone: z.string().min(5).max(32).nullish(),
    message: z.string().max(2000).nullish(),
})
    .superRefine((val, ctx) => {
    // A callback lead is useless without a way to call back.
    if (val.channel === 'callback' && !val.phone) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['phone'],
            message: 'phone is required for a callback lead',
        });
    }
});
/**
 * Seller/admin ANALYTICS view of a listing (market research §4): views, saves,
 * leads broken down by channel, and days-on-market.
 */
export const ListingAnalyticsSchema = z.object({
    listingId: z.string(),
    status: ListingStatusSchema,
    views: z.number().int().nonnegative(),
    saves: z.number().int().nonnegative(),
    leadCount: z.number().int().nonnegative(),
    leadsByChannel: z.record(LeadChannelSchema, z.number().int().nonnegative()),
    /** Whole days since `publishedAt` (null while never published). */
    daysOnMarket: z.number().int().nonnegative().nullable(),
});
/**
 * A buyer SAVE of a listing (`marketplace_saves` collection) — ONE row per
 * (listingId, userId) pair, mirroring the per-user `preferred_cars` relation
 * used for auction cars.
 *
 * The relation, not a bare counter, is the source of truth: it is what makes a
 * save idempotent (a buyer hammering the button cannot inflate the seller's
 * number), un-savable, and answerable for "did I save this?" on page load.
 */
export const ListingSaveSchema = z.object({
    _id: z.unknown().nullish(),
    listingId: z.string(),
    /** Session user id — never taken from the request body. */
    userId: z.string(),
    createdAt: z.coerce.date().nullish(),
});
/**
 * Answer of POST/DELETE/GET .../saves: the CALLER's own save state plus the
 * listing's total. `saves` is the authoritative count of relation rows, so the
 * number the buyer sees and the number the seller's analytics panel shows can
 * never disagree.
 */
export const ListingSaveStateSchema = z.object({
    listingId: z.string(),
    /** Whether the CURRENT user has this listing saved. */
    saved: z.boolean(),
    saves: z.number().int().nonnegative(),
});
/**
 * AUTHORITATIVE per-seller yearly sale count (GET .../listings/sales-count).
 *
 * The Ukrainian PIT ladder brackets a private sale by its ORDINAL within the
 * calendar year (1st → 0%, 2nd → 5+5%, 3rd+ → 18+5%), so the ordinal must not
 * be derived from anything the browser owns. The server counts `sold` listings
 * for the seller in `year`; the UI keeps only the ladder itself.
 */
export const SellerSalesCountSchema = z.object({
    sellerId: z.string(),
    /** Calendar year the count covers (UTC). */
    year: z.number().int(),
    /** Listings that reached `sold` for this seller within `year`. */
    soldCount: z.number().int().nonnegative(),
});
/**
 * `listing.status_changed` outbox event — appended in the SAME transaction as
 * the status flip (transactional-outbox guarantee, same as the relay desk).
 */
export const ListingStatusChangedEventSchema = z.object({
    type: z.literal('listing.status_changed'),
    data: z.object({
        listingId: z.string(),
        listingMode: ListingModeSchema,
        sellerId: z.string(),
        from: ListingStatusSchema,
        to: ListingStatusSchema,
        /** Who performed the transition (seller or admin userId). */
        who: z.string(),
        when: z.string(), // ISO timestamp
        /** True when this was an admin override off the forward ladder. */
        override: z.boolean(),
        /** Mandatory when `override` is true or the target is `rejected`. */
        reason: z.string().nullish(),
        /** Computed consignment fee snapshot on `sold` (gated — never a settlement). */
        consignmentFee: z.number().nullish(),
    }),
});
/**
 * Resolve the consignment fee for a sold price under the marketplace rules.
 * Pure: `fee = clamp(0, cap, feePct × max(0, salePrice)) + listingFee`. A null
 * `cap` means uncapped. Own-stock sales carry NO consignment fee (the platform
 * is the seller) — callers pass this only for `consignment` listings.
 */
export function resolveConsignmentFee(rule, salePrice) {
    const raw = rule.consignmentFeePct * Math.max(0, salePrice);
    const capped = rule.consignmentFeeCap != null ? Math.min(rule.consignmentFeeCap, raw) : raw;
    return capped + (rule.listingFee ?? 0);
}
//# sourceMappingURL=listing.js.map